import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, X } from 'lucide-react';
import './SupplierCombobox.css';

interface SupplierComboboxProps {
    value: string;
    onChange: (value: string) => void;
    suggestions: string[];
    placeholder?: string;
    hint?: string;
}

const SupplierCombobox: React.FC<SupplierComboboxProps> = ({
    value,
    onChange,
    suggestions,
    placeholder = 'Select or type a supplier',
    hint,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [filterText, setFilterText] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Close on outside click
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setIsOpen(false);
                setFilterText('');
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const sortedSuggestions = [...suggestions].sort();
    const filtered = filterText
        ? sortedSuggestions.filter(s => s.toLowerCase().includes(filterText.toLowerCase()))
        : sortedSuggestions;

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        setFilterText(val);
        onChange(val);
        if (!isOpen) setIsOpen(true);
    };

    const handleSelect = (supplier: string) => {
        onChange(supplier);
        setFilterText('');
        setIsOpen(false);
    };

    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation();
        onChange('');
        setFilterText('');
        inputRef.current?.focus();
    };

    const handleToggle = () => {
        setIsOpen(!isOpen);
        if (!isOpen) {
            setFilterText('');
            inputRef.current?.focus();
        }
    };

    return (
        <div className="supplier-combobox" ref={containerRef}>
            <div className={`supplier-combobox-input-wrap ${isOpen ? 'open' : ''}`}>
                <input
                    ref={inputRef}
                    type="text"
                    className="supplier-combobox-input"
                    value={isOpen ? filterText || value : value}
                    onChange={handleInputChange}
                    onFocus={() => setIsOpen(true)}
                    placeholder={placeholder}
                />
                <div className="supplier-combobox-actions">
                    {value && (
                        <button
                            type="button"
                            className="supplier-combobox-clear"
                            onClick={handleClear}
                            tabIndex={-1}
                        >
                            <X size={14} />
                        </button>
                    )}
                    <button
                        type="button"
                        className="supplier-combobox-toggle"
                        onClick={handleToggle}
                        tabIndex={-1}
                    >
                        <ChevronDown size={16} className={isOpen ? 'rotated' : ''} />
                    </button>
                </div>
            </div>

            {isOpen && (
                <div className="supplier-combobox-dropdown">
                    {filtered.length > 0 ? (
                        filtered.map(s => (
                            <button
                                key={s}
                                type="button"
                                className={`supplier-combobox-option ${s === value ? 'selected' : ''}`}
                                onClick={() => handleSelect(s)}
                            >
                                {s}
                            </button>
                        ))
                    ) : (
                        <div className="supplier-combobox-empty">
                            {filterText ? (
                                <span>
                                    No match — press Enter or click away to use "<strong>{filterText}</strong>"
                                </span>
                            ) : (
                                <span>No suppliers in database yet</span>
                            )}
                        </div>
                    )}
                </div>
            )}

            {hint && <span className="field-hint">{hint}</span>}
        </div>
    );
};

export default SupplierCombobox;
